import { ChangeEvent } from 'react';
import { Controller } from 'react-hook-form';
import {
  ClickOutsideDetector,
  IconClose,
  IconDown,
  IconSelected,
  IconUp,
  InputSize,
  InputText,
} from '@ui/src';
import InfoRow from '../../../common/info-row';
import { NewApplicationStatus } from '../../../../../hoc/new-application/types';
import useSourceOfIncome from '../../hooks/use-source-of-income';
import { SourceOfIncomeItemTypes } from '../types';

import cn from '../../styles.module.css';

const SourceOfIncomeItem = ({
  sourceOfIncomes,
  sourceOfIncomeFiltered,
  sourceOfIncome,
  sourceOfIncomeValues,
  questionnarieSetValue,
  baseCurrency,
  questionnarieControl,
  questionnarieErrors,
  questionnarieClearErrors,
  isEditable,
  status,
}: SourceOfIncomeItemTypes) => {
  const { setListVisible, listVisible, formValues, onChange, onDelete } =
    useSourceOfIncome({
      sourceOfIncomeValues,
      sourceOfIncomeValue: sourceOfIncome,
      questionnarieSetValue,
    });

  return (
    <div className={cn.sourceOfIncomeItem}>
      <div className={cn.sourceOfIncomeItemTitle}>
        <ClickOutsideDetector setOptionsListVisible={setListVisible}>
          <>
            <button
              type="button"
              className={cn.sourceOfIncomeButton}
              onClick={() => setListVisible(!listVisible)}
              disabled={Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )}
            >
              {formValues?.sourceId
                ? sourceOfIncomes?.find(
                    (item) => item.id === formValues?.sourceId,
                  )?.value
                : 'Select option'}
              {status !== NewApplicationStatus.ACCEPTED &&
                status !== NewApplicationStatus.DECLINED && (
                  <>
                    {listVisible ? (
                      <IconUp width={13} height={13} />
                    ) : (
                      <IconDown width={13} height={13} />
                    )}
                  </>
                )}
            </button>
            {listVisible && (
              <div className={cn.sourcesOfIncomeValuesList}>
                {formValues?.sourceId && (
                  <button
                    type="button"
                    className={`${cn.sourcesOfIncomeValuesButton} ${cn.active}`}
                  >
                    {
                      sourceOfIncomes?.find(
                        (item) => item.id === formValues?.sourceId,
                      )?.value
                    }
                    <IconSelected />
                  </button>
                )}
                {sourceOfIncomeFiltered
                  ?.filter((item) => item.id !== formValues?.sourceId)
                  ?.map((item) => (
                    <button
                      type="button"
                      className={cn.sourcesOfIncomeValuesButton}
                      key={item.id}
                      onClick={() =>
                        onChange({
                          target: {
                            name: `sourceId-${sourceOfIncome?.id}`,
                            value: item.id,
                          },
                        } as ChangeEvent<HTMLInputElement>)
                      }
                    >
                      {item.value}
                    </button>
                  ))}
              </div>
            )}
          </>
        </ClickOutsideDetector>
      </div>
      <div className={cn.sourceOfIncomeValue}>
        <InputText
          name={`value-${sourceOfIncome?.id}`}
          placeholder="Enter annual income..."
          value={formValues?.value}
          size={InputSize.SMALL}
          className="borderless"
          onChange={(e) =>
            onChange({
              target: {
                name: `value-${sourceOfIncome?.id}`,
                value: e.target.value,
              },
            } as ChangeEvent<HTMLInputElement>)
          }
          currency={baseCurrency}
          disabled={Boolean(
            status === NewApplicationStatus.ACCEPTED ||
            status === NewApplicationStatus.DECLINED,
          )}
          amountInput
        />
        {status !== NewApplicationStatus.ACCEPTED &&
          status !== NewApplicationStatus.DECLINED &&
          (sourceOfIncomeValues?.length as number) > 1 && (
            <button
              type="button"
              className={cn.removeSourceOfIncomeButton}
              onClick={onDelete}
            >
              <IconClose width={16} height={16} />
            </button>
          )}
      </div>
      {formValues?.sourceId === '971c3f8c-847b-4a8d-9ddc-f311ebf55152' && (
        <div className={cn.additionalSourceOfIncomeRow}>
          <InfoRow
            title="Specify other source"
            id="sourcesOfIncomesOther"
            withoutPadding
          >
            <Controller
              control={questionnarieControl}
              name="sourcesOfIncomesOther"
              defaultValue=""
              render={({ field: { onChange: onChangeValue, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Describe details..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChangeValue(e);
                    questionnarieClearErrors(['sourcesOfIncomesOther']);
                  }}
                  className="borderless"
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.sourcesOfIncomesOther?.message}
                />
              )}
            />
          </InfoRow>
        </div>
      )}
      {formValues?.sourceId === 'fe87dd03-1bee-483b-82f2-d0c2df0e3ed6' && (
        <div className={cn.additionalSourceOfIncomeRow}>
          <InfoRow
            title="Specify other source"
            id="sourcesOfIncomesBorrowedOther"
            withoutPadding
          >
            <Controller
              control={questionnarieControl}
              name="sourcesOfIncomesBorrowedOther"
              defaultValue=""
              render={({ field: { onChange: onChangeValue, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Describe details..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChangeValue(e);
                    questionnarieClearErrors(['sourcesOfIncomesBorrowedOther']);
                  }}
                  className="borderless"
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.sourcesOfIncomesBorrowedOther?.message
                  }
                />
              )}
            />
          </InfoRow>
        </div>
      )}
    </div>
  );
};

export default SourceOfIncomeItem;
